import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { urlSeguraONull } from '../utils/urlSegura';
import { collection, query, where, getDocs, doc, getDoc, addDoc } from "firebase/firestore";
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
L.Marker.prototype.options.icon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconSize: [25,41], iconAnchor: [12,41] });

// Movemos los estilos arriba para evitar problemas de carga
const msgEstilo = { padding: '100px', textAlign: 'center', fontFamily: 'sans-serif' };
const badgeEstilo = { color: '#007bff', fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' };
const btnAction = { padding: '12px 25px', background: '#1a1a1a', color: 'white', textDecoration: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '14px', display: 'inline-block' };
const btnWeb = { ...btnAction, background: 'white', color: '#1a1a1a', border: '1px solid #ddd' };
const gridEstilo = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '25px' };
const cardProp = { background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' };

export default function CorredorDetalle() {
  // La página se llega por /:slug (URL amigable) o por /corredor/:id (legacy).
  const { id: paramId, slug: paramSlug } = useParams();
  const navigate = useNavigate();
  const [corredor, setCorredor] = useState(null);
  const [idReal, setIdReal] = useState(null);   // doc id real del corredor en Firestore
  const [propiedades, setPropiedades] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [opiniones, setOpiniones] = useState([]);
  const [formOp, setFormOp] = useState({ nombre: '', email: '', estrellas: 5, comentario: '' });
  const [enviandoOp, setEnviandoOp] = useState(false);
  const [opEnviada, setOpEnviada] = useState(false);
  const [opError, setOpError] = useState('');
  const [paginaC, setPaginaC] = useState(1);
  const [fotoAmpliada, setFotoAmpliada] = useState(null);  // lightbox de fotos de oficina
  const POR_PAGINA_C = 12;

  useEffect(() => {
    async function cargarDatos() {
      // 1. Resolver el corredor por slug (URL amigable) o por id (legacy)
      let docId = null;
      let data = null;
      try {
        if (paramSlug) {
          const sSnap = await getDocs(query(collection(db, "vendedores"), where("slug", "==", paramSlug)));
          if (!sSnap.empty) { docId = sSnap.docs[0].id; data = sSnap.docs[0].data(); }
        } else if (paramId && paramId !== "undefined") {
          const vSnap = await getDoc(doc(db, "vendedores", paramId));
          if (vSnap.exists()) { docId = vSnap.id; data = vSnap.data(); }
        }
      } catch (error) {
        console.error("Error resolviendo corredor:", error);
      }

      if (!docId) { setCargando(false); return; }

      // Si entraron por /corredor/:id y el corredor ya tiene slug, redirigir a la URL amigable
      if (paramId && data?.slug) {
        navigate(`/${data.slug}`, { replace: true });
        return;
      }

      setCorredor(data);
      setIdReal(docId);

      try {
        // 2. Obtener su cartera de propiedades publicadas
        // Nota: filtramos client-side con publicada !== false (igual que el panel admin)
        // para no depender de igualdad estricta boolean en Firestore.
        const q = query(collection(db, "propiedades"), where("vendedorId", "==", docId));
        const pSnap = await getDocs(q);
        const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
        setPropiedades(
          pSnap.docs
            .map(d => ({ ...d.data(), id: d.id }))
            .filter(p =>
              !p.dadaDeBaja &&
              !p.eliminada &&
              p.publicada !== false &&
              (p.fechaCaducidad ? (new Date(p.fechaCaducidad) >= hoy || p.renovacionAutomatica) : !!p.renovacionAutomatica)
            )
        );

        // 3. Agentes: se leen de la copia pública en el doc del corredor
        //    (vendedores.agentesPublicos). NO se consulta `usuarios`, que es
        //    privada (lectura solo autenticada) — hacerlo rompía esta sección y,
        //    de paso, bloqueaba la carga de opiniones para el público.
        setAgentes(Array.isArray(data.agentesPublicos) ? data.agentesPublicos : []);

        // 4. Opiniones aprobadas (filtro client-side para evitar índice compuesto)
        const opSnap = await getDocs(query(collection(db, 'opiniones'), where('corredorId', '==', docId)));
        const ops = opSnap.docs
          .map(d => ({ ...d.data(), id: d.id }))
          .filter(o => o.aprobada === true)
          .sort((a, b) => {
            const fa = a.creadoEn?.toDate ? a.creadoEn.toDate() : a.creadoEn ? new Date(a.creadoEn) : new Date(0);
            const fb = b.creadoEn?.toDate ? b.creadoEn.toDate() : b.creadoEn ? new Date(b.creadoEn) : new Date(0);
            return fb - fa;
          });
        setOpiniones(ops);
      } catch (error) {
        console.error("Error de base de datos:", error);
      }
      setCargando(false);
    }

    cargarDatos();
  }, [paramId, paramSlug, navigate]);

  if (cargando) return <div style={msgEstilo}>Cargando perfil del profesional...</div>;

  if (!corredor) return (
    <div style={msgEstilo}>
      <h2>Perfil no encontrado</h2>
      <p>Es posible que el corredor aún no haya configurado sus datos en el Panel.</p>
      <Link to="/" style={{color: '#007bff'}}>Volver al inicio</Link>
    </div>
  );

  const totalPaginasC = Math.ceil(propiedades.length / POR_PAGINA_C);
  const propsPaginaC  = propiedades.slice((paginaC - 1) * POR_PAGINA_C, paginaC * POR_PAGINA_C);

  const irPagina = (n) => { setPaginaC(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const enviarOpinion = async (e) => {
    e.preventDefault();
    if (!formOp.nombre.trim() || !formOp.comentario.trim()) { setOpError('El nombre y el comentario son obligatorios.'); return; }
    if (formOp.comentario.trim().length < 10) { setOpError('El comentario debe tener al menos 10 caracteres.'); return; }
    setEnviandoOp(true); setOpError('');
    try {
      await addDoc(collection(db, 'opiniones'), {
        corredorId: idReal,
        corredorNombre: corredor.nombre || '',
        autorNombre: formOp.nombre.trim(),
        autorEmail: formOp.email.trim(),
        estrellas: formOp.estrellas,
        comentario: formOp.comentario.trim(),
        aprobada: false,
        creadoEn: new Date(),
      });
      setOpEnviada(true);
    } catch { setOpError('Error al enviar. Intentá de nuevo más tarde.'); }
    setEnviandoOp(false);
  };

  return (
    <div style={{ padding: '40px', maxWidth: '1100px', margin: '0 auto' }}>
      {/* ── PORTADA + CABECERA (mini-web del corredor) ── */}
      <div style={{ marginBottom: '40px', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', background: 'white' }}>
        {/* Foto de portada (o degradado de marca si no hay) */}
        <div style={{
          height: '240px',
          background: corredor.portadaUrl
            ? `#e9edf2 center / cover no-repeat url("${corredor.portadaUrl}")`
            : 'linear-gradient(120deg, #1a1a2e 0%, #0d6efd 100%)',
        }} />

        {/* Cabecera: logo grande montado sobre la portada + datos */}
        <div style={{ padding: '0 40px 32px', display: 'flex', gap: '32px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', flexShrink: 0, marginTop: '-70px' }}>
            {corredor.logoUrl ? (
              <img src={corredor.logoUrl} alt="Logo" style={{ width: '180px', height: '140px', objectFit: 'contain', background: 'white', borderRadius: '16px', border: '1px solid #eee', padding: '12px', boxSizing: 'border-box', boxShadow: '0 6px 20px rgba(0,0,0,0.12)' }} />
            ) : corredor.fotoPerfilUrl ? (
              <img src={corredor.fotoPerfilUrl} alt="Foto" style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '20px', border: '4px solid white', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }} />
            ) : (
              <div style={{ width: '150px', height: '150px', borderRadius: '20px', background: '#f0f0f0', border: '4px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '56px', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }}>🏢</div>
            )}
            {/* Si hay logo Y foto personal, la foto del responsable va chica debajo */}
            {corredor.logoUrl && corredor.fotoPerfilUrl && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={corredor.fotoPerfilUrl} alt="Responsable" style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '50%', border: '2px solid #eee', flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: '#888' }}>Responsable</span>
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: '260px', paddingTop: '24px' }}>
          <span style={badgeEstilo}>Corredor Inmobiliario</span>
          <h1 style={{ margin: '5px 0', fontSize: '32px' }}>{corredor.nombre || "Profesional"}</h1>
          <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
            {corredor.bio || "Corredor inmobiliario asociado a la CIF."}
          </p>
          
          {/* Números de contacto */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '18px' }}>
            {corredor.whatsapp && (
              <p style={{ margin: 0, fontSize: '14px', color: '#444' }}>
                💬 WhatsApp: <strong>+{corredor.whatsapp}</strong>
              </p>
            )}
            {corredor.telefonoFijo && (
              <p style={{ margin: 0, fontSize: '14px', color: '#444' }}>
                📞 Tel. oficina: <strong>{corredor.telefonoFijo}</strong>
              </p>
            )}
          </div>

          {/* Botones de acción */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {corredor.whatsapp && (
              <a href={`https://wa.me/${corredor.whatsapp}`} target="_blank" rel="noreferrer" style={btnAction}>
                💬 Contactar por WhatsApp
              </a>
            )}
            {corredor.telefonoFijo && (
              <a href={`tel:${corredor.telefonoFijo.replace(/\D/g, '')}`} style={btnWeb}>
                📞 Llamar
              </a>
            )}
            {urlSeguraONull(corredor.sitioWeb) && (
              <a href={urlSeguraONull(corredor.sitioWeb)} target="_blank" rel="noreferrer" style={btnWeb}>🌐 Sitio Web</a>
            )}
            {urlSeguraONull(corredor.instagram) && (
              <a href={urlSeguraONull(corredor.instagram)} target="_blank" rel="noreferrer" style={btnWeb}>📸 Instagram</a>
            )}
            {urlSeguraONull(corredor.facebook) && (
              <a href={urlSeguraONull(corredor.facebook)} target="_blank" rel="noreferrer" style={btnWeb}>👥 Facebook</a>
            )}
            {urlSeguraONull(corredor.tiktok) && (
              <a href={urlSeguraONull(corredor.tiktok)} target="_blank" rel="noreferrer" style={btnWeb}>🎵 TikTok</a>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* ── INFORMACIÓN DE LA OFICINA ── */}
      {(corredor.domicilio || corredor.fotosOficina?.length > 0 || corredor.lat) && (
        <div style={{ background: 'white', padding: '30px', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', marginBottom: '40px' }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#1a1a1a' }}>Información de la Oficina</h3>

          {(corredor.domicilio || urlSeguraONull(corredor.ubicacionMap) || corredor.lat) && (
            <div style={{ display: 'grid', gridTemplateColumns: corredor.lat ? 'repeat(auto-fit, minmax(280px, 1fr))' : '1fr', gap: '30px', alignItems: 'start', marginBottom: corredor.fotosOficina?.length > 0 ? '28px' : 0 }}>
              <div>
                {(corredor.domicilio || corredor.localidad) && (
                  <p style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#444', margin: '0 0 14px 0', fontSize: '15px' }}>
                    <span>📍</span>
                    <span>
                      {corredor.domicilio}
                      {corredor.domicilio && corredor.localidad && ' · '}
                      {corredor.localidad}
                    </span>
                  </p>
                )}
                {urlSeguraONull(corredor.ubicacionMap) && (
                  <a href={urlSeguraONull(corredor.ubicacionMap)} target="_blank" rel="noreferrer"
                    style={{ display: 'inline-block', padding: '8px 16px', background: '#f0f4ff', color: '#007bff', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' }}>
                    🗺️ Ver en Google Maps
                  </a>
                )}
              </div>

              {corredor.lat && (
                <div style={{ height: '260px', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }}>
                  <MapContainer center={[corredor.lat, corredor.lng]} zoom={15} style={{ height: '100%' }} scrollWheelZoom={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                    <Marker position={[corredor.lat, corredor.lng]} />
                  </MapContainer>
                </div>
              )}
            </div>
          )}

          {/* Galería de la oficina en grande (click para ampliar) */}
          {corredor.fotosOficina?.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px' }}>
              {corredor.fotosOficina.map((url, i) => (
                <button key={i} type="button" onClick={() => setFotoAmpliada(url)}
                  style={{ padding: 0, border: 'none', borderRadius: '12px', overflow: 'hidden', cursor: 'zoom-in', background: '#f0f0f0', boxShadow: '0 4px 14px rgba(0,0,0,0.08)', gridColumn: (i === 0 && corredor.fotosOficina.length > 2) ? 'span 2' : 'auto' }}>
                  <img src={url} alt={`oficina ${i + 1}`}
                    style={{ display: 'block', width: '100%', height: (i === 0 && corredor.fotosOficina.length > 2) ? '320px' : '220px', objectFit: 'cover' }} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lightbox de fotos de oficina */}
      {fotoAmpliada && (
        <div onClick={() => setFotoAmpliada(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px', cursor: 'zoom-out' }}>
          <button type="button" onClick={() => setFotoAmpliada(null)} aria-label="Cerrar"
            style={{ position: 'absolute', top: '20px', right: '24px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '50%', width: '44px', height: '44px', fontSize: '24px', cursor: 'pointer', lineHeight: 1 }}>×</button>
          <img src={fotoAmpliada} alt="Foto de la oficina ampliada"
            style={{ maxWidth: '95%', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }} />
        </div>
      )}

      {/* ── EQUIPO DE AGENTES ── */}
      {agentes.length > 0 && (
        <div style={{ background: 'white', padding: '30px', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', marginBottom: '40px' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#1a1a1a' }}>
            👥 Equipo de trabajo
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            {agentes.map(a => (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                background: '#f8f9fa', borderRadius: '14px', padding: '14px 20px',
                border: '1px solid #e9ecef', minWidth: '190px',
              }}>
                {a.fotoUrl
                  ? <img src={a.fotoUrl} alt={a.nombre}
                      style={{ width: '54px', height: '54px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e5e7eb', flexShrink: 0 }} />
                  : <div style={{ width: '54px', height: '54px', borderRadius: '50%', background: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>👤</div>
                }
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: '0 0 2px', fontWeight: '700', fontSize: '14px', color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {a.nombre}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>Agente inmobiliario</p>
                  {a.telefono && (
                    <a href={`https://wa.me/${a.telefono.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                      style={{ fontSize: '11px', color: '#25d366', fontWeight: '700', textDecoration: 'none' }}>
                      WhatsApp →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', marginBottom: '25px', borderBottom: '2px solid #eee', paddingBottom: '10px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Propiedades de este Corredor</h2>
        {propiedades.length > 0 && (
          <span style={{ fontSize: '13px', color: '#888' }}>
            {propiedades.length} publicada{propiedades.length !== 1 ? 's' : ''}
            {totalPaginasC > 1 && ` · página ${paginaC} de ${totalPaginasC}`}
          </span>
        )}
      </div>

      {propiedades.length === 0
        ? <p style={{ color: '#888' }}>Este corredor no tiene propiedades publicadas actualmente.</p>
        : (
          <>
            <div style={gridEstilo}>
              {propsPaginaC.map(p => (
                <Link to={`/propiedad/${p.id}`} key={p.id} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={cardProp}>
                    <img src={p.fotoUrl || '/sin-imagen.svg'} onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = '/sin-imagen.svg'; }} alt="prop" style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                    <div style={{ padding: '15px' }}>
                      <h4 style={{ margin: '0 0 5px 0' }}>{p.titulo || p.nombre || "Propiedad"}</h4>
                      <p style={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                        {p.moneda || '$'} {p.precio ? Number(p.precio).toLocaleString('es-AR') : 'Consultar'}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Paginación */}
            {totalPaginasC > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', marginTop: '32px', flexWrap: 'wrap' }}>
                <button onClick={() => irPagina(paginaC - 1)} disabled={paginaC === 1}
                  style={{ padding: '9px 18px', border: '1px solid #ddd', borderRadius: '8px', background: paginaC === 1 ? '#f8f9fa' : 'white', color: paginaC === 1 ? '#ccc' : '#333', cursor: paginaC === 1 ? 'default' : 'pointer', fontWeight: 'bold', fontSize: '14px' }}>
                  ← Anterior
                </button>
                {Array.from({ length: totalPaginasC }, (_, i) => i + 1)
                  .filter(n => n === 1 || n === totalPaginasC || Math.abs(n - paginaC) <= 1)
                  .reduce((acc, n, i, arr) => { if (i > 0 && n - arr[i - 1] > 1) acc.push('…'); acc.push(n); return acc; }, [])
                  .map((n, i) => n === '…'
                    ? <span key={`e${i}`} style={{ padding: '9px 6px', color: '#999', fontSize: '14px' }}>…</span>
                    : <button key={n} onClick={() => irPagina(n)}
                        style={{ padding: '9px 15px', border: '1px solid', borderColor: n === paginaC ? '#007bff' : '#ddd', borderRadius: '8px', background: n === paginaC ? '#007bff' : 'white', color: n === paginaC ? 'white' : '#333', cursor: n === paginaC ? 'default' : 'pointer', fontWeight: n === paginaC ? 'bold' : 'normal', fontSize: '14px' }}>
                        {n}
                      </button>
                  )}
                <button onClick={() => irPagina(paginaC + 1)} disabled={paginaC === totalPaginasC}
                  style={{ padding: '9px 18px', border: '1px solid #ddd', borderRadius: '8px', background: paginaC === totalPaginasC ? '#f8f9fa' : 'white', color: paginaC === totalPaginasC ? '#ccc' : '#333', cursor: paginaC === totalPaginasC ? 'default' : 'pointer', fontWeight: 'bold', fontSize: '14px' }}>
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )
      }

      {/* ── OPINIONES ── */}
      <div style={{ background: 'white', padding: '30px', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', marginTop: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '6px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>⭐ Opiniones de clientes</h2>
          {opiniones.length > 0 && (
            <span style={{ background: '#fffbeb', color: '#d97706', padding: '4px 14px', borderRadius: '12px', fontWeight: '900', fontSize: '15px', border: '1px solid #fcd34d' }}>
              {(opiniones.reduce((s, o) => s + o.estrellas, 0) / opiniones.length).toFixed(1)} ★ · {opiniones.length} opinión{opiniones.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>
        <p style={{ color: '#888', fontSize: '14px', margin: '0 0 24px' }}>
          {opiniones.length > 0 ? 'Opiniones verificadas por el equipo CIF.' : 'Todavía no hay opiniones sobre este corredor.'}
        </p>

        {opiniones.map(op => {
          const fecha = op.creadoEn?.toDate ? op.creadoEn.toDate() : op.creadoEn ? new Date(op.creadoEn) : null;
          return (
            <div key={op.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                <div>
                  <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a1a1a' }}>{op.autorNombre}</span>
                  <span style={{ marginLeft: '10px', color: '#f59e0b', fontSize: '17px', letterSpacing: '1px' }}>
                    {'★'.repeat(op.estrellas)}{'☆'.repeat(5 - op.estrellas)}
                  </span>
                </div>
                {fecha && <span style={{ fontSize: '12px', color: '#aaa' }}>{fecha.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>}
              </div>
              <p style={{ margin: 0, fontSize: '14px', color: '#555', lineHeight: 1.6 }}>{op.comentario}</p>
            </div>
          );
        })}

        {/* Formulario */}
        <div style={{ marginTop: opiniones.length > 0 ? '24px' : '4px', borderTop: opiniones.length > 0 ? '2px solid #f0f0f0' : 'none', paddingTop: opiniones.length > 0 ? '28px' : '0' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#333' }}>📝 Dejá tu opinión</h3>
          {opEnviada ? (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', padding: '18px 20px', color: '#166534', fontSize: '14px', lineHeight: 1.6 }}>
              <strong>✅ ¡Gracias por tu opinión!</strong><br />
              Tu comentario fue recibido y será publicado una vez que el equipo lo revise.
            </div>
          ) : (
            <form onSubmit={enviarOpinion}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '3px' }}>Tu nombre *</label>
                  <input value={formOp.nombre} onChange={e => setFormOp(p => ({ ...p, nombre: e.target.value }))}
                    placeholder="Nombre y apellido" required
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '3px' }}>Email (opcional)</label>
                  <input type="email" value={formOp.email} onChange={e => setFormOp(p => ({ ...p, email: e.target.value }))}
                    placeholder="tucorreo@ejemplo.com"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '6px' }}>Calificación *</label>
                <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" onClick={() => setFormOp(p => ({ ...p, estrellas: n }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '32px', padding: '0 2px', color: n <= formOp.estrellas ? '#f59e0b' : '#d1d5db', lineHeight: 1 }}>★</button>
                  ))}
                  <span style={{ marginLeft: '10px', fontSize: '13px', color: '#888' }}>
                    {['', 'Muy malo', 'Malo', 'Regular', 'Bueno', 'Excelente'][formOp.estrellas]}
                  </span>
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '3px' }}>Comentario *</label>
                <textarea value={formOp.comentario} onChange={e => setFormOp(p => ({ ...p, comentario: e.target.value }))}
                  placeholder="Contá tu experiencia con este corredor..." required rows={3}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              {opError && <p style={{ color: '#dc2626', fontSize: '13px', background: '#fff5f5', border: '1px solid #fca5a5', borderRadius: '6px', padding: '8px 12px', margin: '0 0 12px' }}>{opError}</p>}
              <button type="submit" disabled={enviandoOp}
                style={{ padding: '11px 28px', background: enviandoOp ? '#6c757d' : '#1a1a2e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '14px', cursor: enviandoOp ? 'wait' : 'pointer' }}>
                {enviandoOp ? '⏳ Enviando...' : '📤 Enviar opinión'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}